// The Customers tab's store filter.
//
// This was an ordinary <select> carrying every location an organization has. That was
// reasonable when an organization had a handful; after the cutover import Marco's alone
// carries over a thousand, and a native dropdown of a thousand options is a control an
// agent cannot use while somebody is on the phone.
//
// So: a text box that narrows, and a list that only renders what matched. The matching
// itself lives in ../../components/create/store-search.js, shared with the store step on
// the new-ticket screen — the two places an agent picks a store should not each have
// their own idea of what "MMP 110" means.
//
// `GET /api/organizations/:id/locations` is unpaginated (docs/API.md §4.2), so every row
// is already here and the filtering is local: no request per keystroke, no spinner, no
// stale-response race.
import { useEffect, useRef, useState } from 'react';

import {
  filterStores, storeCountLabel, MAX_VISIBLE_STORES,
} from '../../components/create/store-search.js';

const labelFor = (location) => (location.account_number
  ? `${location.name} · ${location.account_number}`
  : location.name);

export default function StoreFilter({ locations, status, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const rows = Array.isArray(locations) ? locations : [];
  const selected = rows.find((row) => String(row.id) === String(value)) ?? null;
  const matches = filterStores(rows, query);
  const visible = matches.slice(0, MAX_VISIBLE_STORES);
  const countLabel = storeCountLabel(matches.length, visible.length, query);

  // Close on a click anywhere else. Without this the panel stays open over the table and
  // the agent has to find the control again to dismiss it.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const choose = (locationId) => {
    onChange(locationId);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="w-52" ref={boxRef}>
      <label className="field-label" htmlFor="customer-location">Store</label>

      <div className="relative">
        <input
          id="customer-location"
          type="search"
          className="field-input"
          disabled={status === 'loading'}
          // Shows what is SELECTED when closed and what is being TYPED when open, so the
          // control always answers "which store am I filtered to?" without a second line.
          value={open ? query : (selected ? labelFor(selected) : '')}
          placeholder={status === 'loading' ? 'Loading stores…' : 'All stores'}
          onFocus={() => { setQuery(''); setOpen(true); }}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') { setOpen(false); event.currentTarget.blur(); }
          }}
          aria-label="Filter by store"
          autoComplete="off"
        />

        {open && (
          <div className="absolute z-20 mt-1 w-[28rem] max-w-[90vw] rounded-md border border-line
                          bg-surface shadow-lg dark:border-line-strong dark:bg-sunken">
            <ul className="max-h-72 overflow-y-auto py-1">
              <li>
                <button
                  type="button"
                  onClick={() => choose('')}
                  className="flex w-full px-3 py-2 text-left text-sm text-ink hover:bg-sunken
                             focus:bg-brand-600 focus:text-white focus:outline-none dark:hover:bg-line"
                >
                  All stores
                </button>
              </li>
              {visible.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => choose(String(row.id))}
                    className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left
                               text-sm text-ink hover:bg-sunken focus:bg-brand-600 focus:text-white
                               focus:outline-none dark:hover:bg-line group"
                  >
                    <span>{row.name}</span>
                    <span className="shrink-0 text-xs text-ink-muted group-focus:text-brand-50">
                      {row.account_number ?? '-'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {countLabel && (
              <p className="border-t border-line px-3 py-1.5 text-xs text-ink-muted dark:border-line-strong">
                {countLabel}
              </p>
            )}
          </div>
        )}
      </div>

      <p className="mt-1 h-4 text-xs text-ink-muted">
        {status === 'error' ? 'Store list unavailable.' : ''}
      </p>
    </div>
  );
}
